
/**
 * The blueprint maps core module, unifies map assembling and data fetching.
 */
let core = {};

const getRetryInterval = () => {
    const minMs = 1000;
    const maxMs = 5000;
    return Math.floor(Math.random() * (maxMs - minMs + 1)) + minMs;
}

/**
 * Fetches json from given endpoint url with a given timeout.
 * @param {string} dataServiceUrl 
 * @returns JSON fetched from supplied address.
 */
core.FetchWithTimeout = async (dataServiceUrl, timeout) => {
    const startDate = new Date()
    let response = { }
    if (!timeout || timeout == 0) {
        response = await fetch(dataServiceUrl);
    }
    else 
    {
        try 
        {        
            response = await fetch(dataServiceUrl, {
              signal: AbortSignal.timeout(timeout)
            });
        }
        catch 
        {
            response = {
                status: 408
            }
        }
    }

    if (dataServiceUrl.indexOf('/api/d') != -1) {
        if (response.status == 200) {
            console.log('OK - Query finished, took: ' + ((new Date()).getTime() - startDate.getTime()) / 1000);
        }
        else if (response.status == 408) {
            console.log('TIMEOUT - Query terminated, took: ' + ((new Date()).getTime() - startDate.getTime()) / 1000);
        }
        else {
            console.log('ERROR - Query finished, took: ' + ((new Date()).getTime() - startDate.getTime()) / 1000);
        }

    }

    return response;
}

/**
 * Fetches json from given endpoint url.
 * @param {string} dataServiceUrl 
 * @returns JSON fetched from supplied address.
 */
core.FetchJson = (dataServiceUrl, maxRetries) => {
    maxRetries ??= 3;
    const startDate = new Date()
    return fetch(dataServiceUrl)
        .then(response => {
            if (response.ok) {
                if (dataServiceUrl.indexOf('/api/d') != -1) {
                    console.log('Query finished, took: ' + ((new Date()).getTime() - startDate.getTime()) / 1000);
                }
                
                return response.json();
            } else if (response.status === 429 && maxRetries > 0) {
                return new Promise((resolve) => {
                    const retryInterval = getRetryInterval();
                    console.log(`An attempt to load ${dataServiceUrl} failed, retrying in a few seconds`);
                    setTimeout(() => { resolve(core.FetchJson(dataServiceUrl, maxRetries - 1)); }, retryInterval);
                });
            }
            throw new Error(response.statusText);
        }, (error) => { throw error });
}

/**
 * Rounds a number leaving a few decimal digits as a fraction.
 * Avoids ugly numbers in reporting (like .33..334)
 * @param {number} numberToRound 
 * @param {number} digits 
 * @returns Number rounded to number of digits supplied.
 */
core.naiveRound = (numberToRound, digits = 2) => {

    if (digits == 0) {
        return Math.round(numberToRound);
    }

    const p = Math.pow(10, digits);
    return Math.round(numberToRound * p) / p;
}

/**
 * Gets an average for numbers in array.
 * @param {Array} array 
 * @returns avergage number rounded to 2 digits.
 */
core.average = (array) => (array.length != 0) ? core.naiveRound(array.reduce((a, b) => a + b) / array.length) : 0;

/**
* 
* @param {Date} date 
* @param {number} seconds 
* @returns {Date}
*/
core.addTo = (date, seconds) => {
   const dateCopy = new Date(date);
   dateCopy.setSeconds(dateCopy.getSeconds() + seconds);
   return dateCopy;
}

core.toBossView = number => {
    let value;
    const abs = Math.abs(number);

    if (number < 1000) {
        value = core.naiveRound(number, 1);
        let result = value.toString();
        return abs == number ? result : `-${result}`;
    }
    if (number >= 1000 && number < 100 * 1000) {
        value = core.toBossView(number / 1000);
        return `${value}K`;
    }
    if (number >= 100 * 1000 & number < 100 * 1000 * 1000) {
        value = core.toBossView(number / (1000 * 1000));
        return `${value}M`;
    }
}

/**
 * Gets a query string value with optional default value.
 * @param {string} key 
 * @param {*} defaultValue 
 * @returns 
 */
core.getQueryString = (key, defaultValue = undefined) => {
    const searchParams = new URLSearchParams(window.location.search);
    const value = searchParams.get(key);

    return value == null ? defaultValue : value;
}

core.getBoolQueryString = (key, defaultValue = undefined) => {
    let value = core.getQueryString(key, defaultValue);
    return core.toBool(value);
}

/**
 * Unifies how dates are shaped to string.
 * @param {number} daysAgo 
 * @returns 
 */
core.dateToString = date => date.toLocaleDateString("fr-CA");

core.dateDaysAgo = daysAgo => {
    let previousDay = new Date();
    previousDay.setDate(previousDay.getDate() - daysAgo);
    return core.dateToString(previousDay);
};

core.wrap = (html, className) => `<div class="${className}">${html}</div>`;

core.toHtml = (jsObject, extra, className) => {
    let summary = new Array();
    let text;
    if (jsObject instanceof Array) {
        for (let j = 0; j < jsObject.length; j++) {
            {
                let elem = jsObject[j];
                summary.push(core.toHtml(elem, [], className));
            }
        }
    } else {
        for (let x in jsObject) {
            if (x.startsWith('_')) {
                continue;
            }
            text = `<b>${x}:  ${jsObject[x]}</b>`;
            summary.push(text);
        }
    }

    if (extra instanceof Array) {
        for (let i = 0; i < extra.length; i += 2) {
            let key = extra[i];
            let value = extra[i + 1];
            text = `<b>${key}: ${value}</b>`;
            summary.push(text);
        }
    }

    text = summary.join('<br/>');

    return core.wrap(text, className);
}

core.toBool = (string) => {
    if (string instanceof Boolean) {
        return string;
    }

    return string === 'false' || string === 'undefined' || string === 'null' || string === '0' ?
        false : !!string;
}
export default core;